/**
 * The flight record (AODB): the airport's record of every movement, the
 * predictions beside it, and the checks on it.
 *
 * Mirrors the platform's movements/record.py, predict.py and messages.py,
 * so the demonstration behaves as the platform does:
 *
 *  - scheduled, estimated, actual and PREDICTED are four separate times; a
 *    prediction always carries its basis in words, the sample behind it and a
 *    confidence, and is never shown as if it were a report;
 *  - a departure waits for its unit: the inbound's time plus the turnaround
 *    the operator actually achieves is a floor under it;
 *  - an operator's estimate is corrected by how that operator's estimates
 *    have turned out;
 *  - the record checks itself — times out of order, an estimate that passed
 *    with nobody reporting, more on board than seats;
 *  - a movement message (MVT) is applied only to a movement on the record,
 *    and held with the reason when it would put the record out of order.
 */

export type MovementDirection = "arrival" | "departure";
export type MovementStatus = "scheduled" | "confirmed" | "delayed" | "departed" | "arrived" | "cancelled" | "diverted";
export type Confidence = "high" | "medium" | "low";

export interface Prediction {
  delayMinutes: number;
  basis: string[];
  confidence: Confidence;
  sample: number;
  model: string;
  at: string;
}

export interface Flag {
  kind: "order" | "stale" | "load" | "status";
  why: string;
}

/**
 * Who told us a flight would be late, when, and the time they gave.
 * Appended to the movement, never edited (platform: movement.notices).
 */
export interface MovementNotice {
  at: string;
  /** airline_occ | ground_handler | atc | schedule_feed | airport_ops */
  source: string;
  by?: string;
  newTime: string;
  code?: string;
  note?: string;
}

export interface Movement {
  id: string;
  direction: MovementDirection;
  carrier: string;
  number: string;
  registration?: string;
  classKey?: string;
  counterpartNode?: string;
  serviceType?: string;
  serviceDate: string;
  status?: MovementStatus;
  scheduledAt?: string;
  estimatedAt?: string;
  actualAt?: string;
  predictedAt?: string;
  prediction?: Prediction;
  /** left_position, departed, arrived, reached_position */
  milestones: Record<string, string>;
  /** position, gate, building, counters, reclaim */
  handling: Record<string, string>;
  /** booked, actual, transfer, cargo_kg, mail_kg */
  loads: Record<string, number>;
  capacity?: number;
  delayCodes: { code: string; minutes?: number; source?: string }[];
  linkedMovementId?: string;
  remarks?: string;
  cancelledReason?: string;
  /** delay notices, oldest first */
  notices?: MovementNotice[];
  flags: Flag[];
  version: number;
  source?: string;
  /**
   * Who or what set each figure last (platform: movement.provenance):
   * `actual_at`, `milestones.left_position`, `loads.actual`, `delay_codes` …
   * → {source: manual (a person) | mvt | aidx | feed | schedule, by, at}.
   */
  provenance?: Record<string, { source: string; by?: string; at?: string }>;
}

export interface MovementEvent {
  at: string;
  field: string;
  old?: string;
  new?: string;
  source: string;
  by?: string;
  note?: string;
}

export interface MovementMessage {
  id: string;
  kind: string;
  raw: string;
  receivedAt: string;
  outcome: "applied" | "refused" | "partial" | "held";
  refusal?: string;
  changes: string[];
  movement?: string;
  movementId?: string;
}

export interface ScheduleLine {
  id?: string;
  season?: string;
  carrier: string;
  number: string;
  direction: MovementDirection;
  validFrom: string;
  validTo: string;
  days: number[];
  localTime: string;
  dayOffset?: number;
  counterpartNode?: string;
  classKey?: string;
  serviceType?: string;
  capacity?: number;
  source?: string;
}

export interface DaySummary {
  serviceDate: string;
  movements: number;
  arrivals: number;
  departures: number;
  flown: number;
  cancelled: number;
  onTimePct: number | null;
  averageDelayMin: number | null;
  delayMinutesByCode: Record<string, number>;
  predictedLate: {
    id: string;
    flight: string;
    direction: MovementDirection;
    delayMinutes: number;
    confidence: Confidence;
  }[];
  flagged: { id: string; flight: string; flags: Flag[] }[];
  loadActual: number;
}

export const MODEL = "history-median v1";
export const LATE_MIN = 15;
export const STALE_MIN = 20;
export const DEFAULT_TURN_MIN = 35;

export interface Past {
  carrier: string;
  number: string;
  direction: MovementDirection;
  scheduledAt: string;
  actualAt: string;
}

const minutes = (a: string | Date, b: string | Date) => (new Date(a).getTime() - new Date(b).getTime()) / 60_000;
const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
function iqr(xs: number[]): number {
  if (xs.length < 4) return Infinity;
  const s = [...xs].sort((a, b) => a - b);
  const q = (p: number) => {
    const i = (s.length - 1) * p;
    const lo = Math.floor(i);
    return s[lo] + (s[Math.min(lo + 1, s.length - 1)] - s[lo]) * (i - lo);
  };
  return q(0.75) - q(0.25);
}
const hhmmUtc = (iso: string) => new Date(iso).toISOString().slice(11, 16);
const sign = (n: number) => `${n >= 0 ? "+" : "−"}${Math.abs(Math.round(n))}`;

/** One movement's prediction, as predict.py `predict_one` makes it. */
export function predictOne(
  m: Movement,
  history: readonly Past[],
  inbound: Movement | undefined,
  bias: { minutes: number | null; n: number },
  turn: { minutes: number; n: number },
  now: Date,
): (Prediction & { predictedAt: string }) | null {
  if (m.actualAt || m.status === "cancelled" || !m.scheduledAt) return null;
  const sched = new Date(m.scheduledAt);
  const hour = sched.getUTCHours();
  const basis: string[] = [];
  let uncorrected = false;
  const delays = (f: (h: Past) => boolean) => history.filter(f).map((h) => minutes(h.actualAt, h.scheduledAt));
  const tiers: [string, number[]][] = [
    [
      `${m.carrier}${m.number} ${m.direction}s over 90 days`,
      delays((h) => h.carrier === m.carrier && h.number === m.number && h.direction === m.direction),
    ],
    [
      `${m.carrier} ${m.direction}s around ${String(hour).padStart(2, "0")}:00`,
      delays(
        (h) =>
          h.carrier === m.carrier &&
          h.direction === m.direction &&
          Math.abs(new Date(h.scheduledAt).getUTCHours() - hour) <= 1,
      ),
    ],
    [
      `all ${m.direction}s around ${String(hour).padStart(2, "0")}:00`,
      delays((h) => h.direction === m.direction && Math.abs(new Date(h.scheduledAt).getUTCHours() - hour) <= 1),
    ],
  ];
  const tier = tiers.find(([, xs]) => xs.length >= 5);
  const xs = tier?.[1] ?? [];
  const histDelay = xs.length ? median(xs) : 0;
  let pred = new Date(sched.getTime() + histDelay * 60_000);
  let sample = xs.length;
  const spread = iqr(xs);
  basis.push(
    tier
      ? `median delay ${sign(histDelay)} min across ${xs.length} ${tier[0]}`
      : "no history yet — the schedule is the starting point",
  );

  if (m.estimatedAt) {
    const est = new Date(m.estimatedAt);
    if (bias.minutes !== null) {
      pred = new Date(est.getTime() + bias.minutes * 60_000);
      basis.push(
        `${m.carrier}'s estimate ${hhmmUtc(m.estimatedAt)} UTC, ${sign(bias.minutes)} min for how its estimates have turned out (${bias.n} past)`,
      );
      sample = Math.max(sample, bias.n);
    } else {
      pred = est;
      basis.push(`${m.carrier}'s estimate ${hhmmUtc(m.estimatedAt)} UTC (too few past estimates to correct it)`);
      /* the history no longer decides the time, so its size cannot vouch for it */
      uncorrected = true;
    }
  }

  if (m.direction === "departure" && inbound && inbound.status !== "cancelled") {
    const inT = inbound.actualAt ?? inbound.predictedAt ?? inbound.estimatedAt;
    if (inT) {
      const floor = new Date(new Date(inT).getTime() + turn.minutes * 60_000);
      if (floor > pred) {
        pred = floor;
        basis.push(
          `the unit ${inbound.actualAt ? "arrived" : "is predicted in"} as ${inbound.carrier}${inbound.number} at ${hhmmUtc(inT)} UTC and needs ${Math.round(turn.minutes)} min to turn (${turn.n >= 5 ? `learned from ${turn.n} turnarounds` : "a default turn"})`,
        );
      }
    }
  }
  let confidence: Confidence = sample >= 20 && spread <= 15 ? "high" : sample >= 5 ? "medium" : "low";
  if (uncorrected && confidence === "high") confidence = "medium";
  if (pred.getTime() < now.getTime() - STALE_MIN * 60_000) {
    confidence = "low";
    basis.push("the predicted time has passed with no actual reported");
  }
  return {
    predictedAt: pred.toISOString(),
    delayMinutes: Math.round(minutes(pred, sched)),
    basis,
    confidence,
    sample,
    model: MODEL,
    at: now.toISOString(),
  };
}

/** What contradicts the record, as predict.py `flags_for` finds it. */
export function flagsFor(m: Movement, byId: Map<string, Movement>, now: Date): Flag[] {
  const out: Flag[] = [];
  const ms = m.milestones ?? {};
  if (ms.left_position && ms.departed && ms.departed < ms.left_position)
    out.push({ kind: "order", why: "airborne before off-block" });
  if (ms.arrived && ms.reached_position && ms.reached_position < ms.arrived)
    out.push({ kind: "order", why: "on-block before touchdown" });
  if (
    m.estimatedAt &&
    !m.actualAt &&
    m.status !== "cancelled" &&
    new Date(m.estimatedAt).getTime() < now.getTime() - STALE_MIN * 60_000
  )
    out.push({
      kind: "stale",
      why: `estimate ${hhmmUtc(m.estimatedAt)} UTC passed ${Math.round(minutes(now, m.estimatedAt))} min ago with no actual`,
    });
  if (m.loads?.actual && m.capacity && m.loads.actual > m.capacity)
    out.push({ kind: "load", why: `${m.loads.actual} on board and ${m.capacity} seats` });
  if (m.status === "cancelled" && m.actualAt)
    out.push({ kind: "status", why: "canceled, and an actual time is on the record" });
  const link = m.linkedMovementId ? byId.get(m.linkedMovementId) : undefined;
  if (m.direction === "departure" && link && m.actualAt && link.actualAt && m.actualAt < link.actualAt)
    out.push({ kind: "order", why: `left before its unit arrived as ${link.carrier}${link.number}` });
  return out;
}

/** Predict and check a day, arrivals first so a departure can lean on its inbound. */
export function runDay(
  moves: Movement[],
  history: readonly Past[],
  biasOf: (carrier: string) => { minutes: number | null; n: number },
  turnOf: (carrier: string) => { minutes: number; n: number },
  now: Date,
): Movement[] {
  const byId = new Map(moves.map((m) => [m.id, { ...m }]));
  const ordered = [...byId.values()].sort((a, b) =>
    a.direction === b.direction ? 0 : a.direction === "arrival" ? -1 : 1,
  );
  for (const m of ordered) {
    const p = predictOne(
      m,
      history,
      m.linkedMovementId ? byId.get(m.linkedMovementId) : undefined,
      biasOf(m.carrier),
      turnOf(m.carrier),
      now,
    );
    if (p) {
      const { predictedAt, ...prediction } = p;
      m.predictedAt = predictedAt;
      m.prediction = prediction;
    }
  }
  for (const m of byId.values()) m.flags = flagsFor(m, byId, now);
  return moves.map((m) => byId.get(m.id)!);
}

export function summarise(moves: readonly Movement[], serviceDate: string): DaySummary {
  const flown = moves.filter((m) => m.actualAt && m.scheduledAt);
  const delays = flown.map((m) => minutes(m.actualAt!, m.scheduledAt!));
  const codes: Record<string, number> = {};
  for (const m of moves) for (const c of m.delayCodes ?? []) codes[c.code] = (codes[c.code] ?? 0) + (c.minutes ?? 0);
  return {
    serviceDate,
    movements: moves.length,
    arrivals: moves.filter((m) => m.direction === "arrival").length,
    departures: moves.filter((m) => m.direction === "departure").length,
    flown: flown.length,
    cancelled: moves.filter((m) => m.status === "cancelled").length,
    onTimePct: delays.length
      ? Math.round((1000 * delays.filter((d) => d <= LATE_MIN).length) / delays.length) / 10
      : null,
    averageDelayMin: delays.length ? Math.round((10 * delays.reduce((a, b) => a + b, 0)) / delays.length) / 10 : null,
    delayMinutesByCode: codes,
    predictedLate: moves
      .filter((m) => !m.actualAt && m.status !== "cancelled" && (m.prediction?.delayMinutes ?? 0) > LATE_MIN)
      .map((m) => ({
        id: m.id,
        flight: `${m.carrier}${m.number}`,
        direction: m.direction,
        delayMinutes: m.prediction!.delayMinutes,
        confidence: m.prediction!.confidence,
      })),
    flagged: moves
      .filter((m) => m.flags.length)
      .map((m) => ({ id: m.id, flight: `${m.carrier}${m.number}`, flags: m.flags })),
    loadActual: moves.reduce((a, m) => a + (m.loads?.actual ?? 0), 0),
  };
}

/* ── movement messages (IATA MVT) ─────────────────────────────────────────── */

export interface ParsedMvt {
  carrier: string;
  number: string;
  day: number;
  registration?: string;
  station: string;
  items: {
    kind: "AD" | "AA" | "EA" | "ED" | "DL" | "PX" | "SI";
    a: string | number | string[];
    b?: string | number[];
  }[];
}

export function parseMvt(raw: string): ParsedMvt {
  let lines = raw
    .trim()
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines[0] && /^MV[TA]$/i.test(lines[0])) lines = lines.slice(1);
  if (!lines.length) throw new Error("an empty message");
  const m = /^([A-Z0-9]{2})([0-9]{1,4}[A-Z]?)\/(\d{2})(?:\.([A-Z0-9-]+))?\.([A-Z]{3,4})$/.exec(lines[0].toUpperCase());
  if (!m) throw new Error(`the flight line ${lines[0]} is not flight/day.registration.station`);
  const out: ParsedMvt = {
    carrier: m[1],
    number: m[2],
    day: Number(m[3]),
    registration: m[4],
    station: m[5],
    items: [],
  };
  for (const ln of lines.slice(1)) {
    const u = ln.toUpperCase();
    for (const t of u.matchAll(/\b(AD|AA)(\d{4})(?:\/(\d{4}))?/g))
      out.items.push({ kind: t[1] as "AD" | "AA", a: t[2], b: t[3] });
    for (const t of u.matchAll(/\bEA(\d{4})\s+([A-Z]{3,4})/g)) out.items.push({ kind: "EA", a: t[1], b: t[2] });
    for (const t of u.matchAll(/\bED(\d{2})?(\d{4})\b/g)) out.items.push({ kind: "ED", a: t[2], b: t[1] });
    if (u.startsWith("DL")) {
      const parts = u.slice(2).split("/");
      out.items.push({
        kind: "DL",
        a: parts.filter((p) => /^\d{2}[A-Z]?$/.test(p)),
        b: parts.filter((p) => /^\d{4}$/.test(p)).map((x) => Number(x.slice(0, 2)) * 60 + Number(x.slice(2))),
      });
    }
    const px = /^PX(\d+)/.exec(u);
    if (px) out.items.push({ kind: "PX", a: Number(px[1]) });
    if (u.startsWith("SI")) out.items.push({ kind: "SI", a: ln.slice(2).trim() });
  }
  return out;
}

/** An MVT time (UTC hhmm) on the service date, moved a day either way if that brings it nearer the schedule. */
export function mvtTime(serviceDate: string, hhmm: string, near?: string): string {
  const base = new Date(`${serviceDate}T${hhmm.slice(0, 2)}:${hhmm.slice(2, 4)}:00Z`).getTime();
  if (!near) return new Date(base).toISOString();
  const n = new Date(near).getTime();
  const best = [-1, 0, 1].map((k) => base + k * 86_400_000).sort((a, b) => Math.abs(a - n) - Math.abs(b - n))[0];
  return new Date(best).toISOString();
}

export type MvtOutcome =
  | { outcome: "applied"; movementId: string; changes: Partial<Movement> }
  | { outcome: "held" | "refused"; movementId?: string; refusal: string };

/** What an MVT would do to the record, as messages.py `apply_mvt` decides it. */
export function applyMvt(raw: string, day: readonly Movement[], stations: readonly string[]): MvtOutcome {
  let p: ParsedMvt;
  try {
    p = parseMvt(raw);
  } catch (e) {
    return { outcome: "refused", refusal: (e as Error).message };
  }
  const ours = new Set(stations.map((s) => s.toUpperCase()));
  const kinds = new Set(p.items.map((i) => i.kind));
  let direction: MovementDirection;
  if (ours.has(p.station) && (kinds.has("AD") || kinds.has("ED"))) direction = "departure";
  else if (ours.has(p.station) && kinds.has("AA")) direction = "arrival";
  else if (p.items.some((i) => i.kind === "EA" && ours.has(String(i.b)))) direction = "arrival";
  else
    return {
      outcome: "refused",
      refusal: `the message is about ${p.station}, not this airport (${[...ours].join(", ")})`,
    };
  const m = day.find(
    (x) =>
      x.carrier === p.carrier &&
      x.number.replace(/^0+/, "") === p.number.replace(/^0+/, "") &&
      x.direction === direction &&
      Number(x.serviceDate.slice(8, 10)) === p.day,
  );
  if (!m)
    return {
      outcome: "held",
      refusal: `no ${direction} ${p.carrier}${p.number} on the record for day ${p.day} — held, not invented`,
    };
  const changes: Partial<Movement> = {};
  const ms: Record<string, string> = {};
  for (const it of p.items) {
    if (it.kind === "AD" && direction === "departure") {
      ms.left_position = mvtTime(m.serviceDate, String(it.a), m.scheduledAt);
      if (it.b) ms.departed = mvtTime(m.serviceDate, String(it.b), m.scheduledAt);
      changes.actualAt = ms.left_position;
      changes.status = "departed";
    } else if (it.kind === "AA" && direction === "arrival") {
      ms.arrived = mvtTime(m.serviceDate, String(it.a), m.scheduledAt);
      if (it.b) ms.reached_position = mvtTime(m.serviceDate, String(it.b), m.scheduledAt);
      changes.actualAt = mvtTime(m.serviceDate, String(it.b ?? it.a), m.scheduledAt);
      changes.status = "arrived";
    } else if (it.kind === "EA" && direction === "arrival" && ours.has(String(it.b))) {
      changes.estimatedAt = mvtTime(m.serviceDate, String(it.a), m.scheduledAt);
    } else if (it.kind === "ED" && direction === "departure") {
      changes.estimatedAt = mvtTime(m.serviceDate, String(it.a), m.scheduledAt);
    } else if (it.kind === "DL") {
      const mins = (it.b as number[]) ?? [];
      changes.delayCodes = (it.a as string[]).map((c, i) => ({ code: c, minutes: mins[i], source: "mvt" }));
    } else if (it.kind === "PX") {
      changes.loads = { ...m.loads, actual: Number(it.a) };
    } else if (it.kind === "SI") {
      changes.remarks = String(it.a);
    }
  }
  if (Object.keys(ms).length) changes.milestones = { ...m.milestones, ...ms };
  if (p.registration && p.registration !== m.registration) changes.registration = p.registration;
  if (
    changes.estimatedAt &&
    m.scheduledAt &&
    minutes(changes.estimatedAt, m.scheduledAt) > LATE_MIN &&
    (!m.status || m.status === "scheduled" || m.status === "confirmed")
  )
    changes.status = "delayed";
  const all = { ...m.milestones, ...(changes.milestones ?? {}) };
  if (all.left_position && all.departed && all.departed < all.left_position)
    return { outcome: "held", movementId: m.id, refusal: "held: airborne before off-block" };
  if (all.arrived && all.reached_position && all.reached_position < all.arrived)
    return { outcome: "held", movementId: m.id, refusal: "held: on-block before touchdown" };
  if (changes.actualAt && m.scheduledAt && Math.abs(minutes(changes.actualAt, m.scheduledAt)) > 20 * 60)
    return {
      outcome: "held",
      movementId: m.id,
      refusal: "held: an actual time twenty hours or more from the schedule — probably the wrong day or flight",
    };
  return { outcome: "applied", movementId: m.id, changes };
}

export const STATUS_LABEL: Record<MovementStatus, string> = {
  scheduled: "Scheduled",
  confirmed: "Confirmed",
  delayed: "Delayed",
  departed: "Departed",
  arrived: "Arrived",
  cancelled: "Canceled",
  diverted: "Diverted",
};
